import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import emotionData from './emotionData.json';
import './emotionSelection.css';
import ReflectionForm from './ReflectionForm';
import { auth, db, collection, addDoc, serverTimestamp } from '../firebase';

export default function EmotionSelection() {
    const navigate = useNavigate();
    const location = useLocation();
    const { moodType, dateStr, otherText } = location.state || { moodType: 'positive' }; // default to positive if navigated directly
    const [selectedEmotions, setSelectedEmotions] = useState([]);
    const [activeTooltip, setActiveTooltip] = useState(null); // stores the emotion key
    const [dictionaryCache, setDictionaryCache] = useState({});
    const [searchQuery, setSearchQuery] = useState('');
    
    const data = emotionData[moodType] || emotionData['positive'];

    const fetchDictionaryData = async (word) => {
        if (dictionaryCache[word] || dictionaryCache[word]?.loading) return;

        setDictionaryCache(prev => ({
            ...prev,
            [word]: { loading: true }
        }));

        try {
            const response = await fetch(`https://api.dictionaryapi.dev/api/v2/entries/en/${word.toLowerCase()}`);
            if (response.ok) {
                const apiData = await response.json();
                const entry = apiData[0];
                const phonetic = entry.phonetic || (entry.phonetics && entry.phonetics.find(p => p.text)?.text) || '';
                const definitions = entry.meanings
                    .flatMap(m => m.definitions)
                    .map(d => d.definition)
                    .slice(0, 3);

                setDictionaryCache(prev => ({
                    ...prev,
                    [word]: { phonetic, definitions, loading: false }
                }));
            } else {
                setDictionaryCache(prev => ({
                    ...prev,
                    [word]: { error: true, loading: false }
                }));
            }
        } catch (err) {
            setDictionaryCache(prev => ({
                ...prev,
                [word]: { error: true, loading: false }
            }));
        }
    };
    console.log(dictionaryCache,"priont dictionaryCache")

    const toggleEmotion = (emotionKey) => {
        if (selectedEmotions.includes(emotionKey)) {
            setSelectedEmotions(selectedEmotions.filter(e => e !== emotionKey));
        } else {
            if (selectedEmotions.length < 5) {
                setSelectedEmotions([...selectedEmotions, emotionKey]);
                setActiveTooltip(null); // hide definition once selected
            } else {
                alert('You can select up to 5 emotions.');
            }
        }
    };

    const handleContextMenu = (e, emotionKey) => {
        e.preventDefault(); // Prevent standard right-click menu or long-press text selection
        if (!selectedEmotions.includes(emotionKey)) {
            setActiveTooltip(emotionKey);
            fetchDictionaryData(emotionKey);
        }
    };

    const handleMouseEnter = (emotionKey) => {
        if (!selectedEmotions.includes(emotionKey)) {
            setActiveTooltip(emotionKey);
            fetchDictionaryData(emotionKey);
        }
    };

    // Optional: Hide tooltip when clicking anywhere else
    const hideTooltip = () => {
        if (activeTooltip) setActiveTooltip(null);
    };

    const handleNext = () => {
        if (selectedEmotions.length > 0) {
            navigate('/reflection-form', { state: { emotion: selectedEmotions.join(', '), dateStr: dateStr, moodType: moodType, otherText: otherText } });
        } else {
            alert('Please select at least one emotion to continue.');
        }
    };



    return (
        <div className="es-page-wrapper" style={{ display: 'flex', flexDirection: 'column', width: '100%', minHeight: '100vh', background: 'linear-gradient(171.75deg, #FFF7F1 38.67%, #FFF7F1 64.36%)', marginTop: '-70px', paddingTop: '70px' }}>

            {/* Top Section: Centered Logo and Title */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', paddingTop: '40px', paddingBottom: '20px', position: 'relative', maxWidth: '1200px', margin: '0 auto' }}>
                <button className="es-back-btn" onClick={() => navigate(-1)} style={{ position: 'absolute', left: '20px', top: '50px' }}>
                    <span className="es-fallback-back">←</span>
                </button>
                <div className="es-title-section" style={{ marginTop: 0, marginBottom: '10px' }}>
                    <div className="es-sun-bg"></div>
                    <img
                        src="/reflections/nabilogo.png"
                        alt="Wings Logo"
                        className="es-logo-img"
                    />
                </div>
                <h1 className="es-title" style={{ marginTop: '10px' }}>My Reflections</h1>
            </div>

            {/* Bottom Section: Two Columns with Separator */}
            <div style={{ display: 'flex', flexDirection: 'row', width: '100%', maxWidth: '1200px', margin: '0 auto', flex: 1, paddingBottom: '40px' }}>

                {/* Left Side: Emotion Selection */}
                <div className="es-container" style={{ flex: 1, borderRight: '1px solid #e0e0e0', background: 'transparent' }} onClick={hideTooltip}>
                    <div className="es-content" style={{ paddingTop: '20px' }}>
                        <div className="es-mood-header">
                            <h2 className="es-mood-title">
                                {data.title} <img src={data.icon} alt={moodType} className="es-mood-icon-small" />
                            </h2>
                            <p className="es-mood-subtitle">{data.subtitle}</p>

                            <div style={{ marginTop: '15px', display: 'flex', justifyContent: 'center' }}>
                                <input 
                                    type="text" 
                                    placeholder="Search emotions..." 
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    style={{
                                        padding: '10px 20px',
                                        borderRadius: '25px',
                                        border: '1px solid #FFBE98',
                                        outline: 'none',
                                        width: '80%',
                                        maxWidth: '300px',
                                        fontFamily: 'inherit',
                                        fontSize: '0.9rem',
                                        color: '#003049',
                                        backgroundColor: '#fffdfb',
                                        boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.05)'
                                    }}
                                />
                            </div>
                        </div>

                        <div className="es-emotions-grid">
                            {Object.keys(data.emotions)
                                .filter(emotionKey => emotionKey.toLowerCase().includes(searchQuery.toLowerCase()))
                                .map((emotionKey) => {
                                const isSelected = selectedEmotions.includes(emotionKey);
                                const emotionInfo = data.emotions[emotionKey];
                                const showTooltip = activeTooltip === emotionKey && !isSelected;

                                return (
                                    <div key={emotionKey} style={{ position: 'relative' }}>
                                        <button
                                            className={`es-emotion-pill ${isSelected ? 'es-emotion-pill-selected' : ''}`}
                                            onClick={(e) => {
                                                e.stopPropagation(); // prevent hideTooltip
                                                toggleEmotion(emotionKey);
                                            }}
                                            onContextMenu={(e) => {
                                                e.stopPropagation();
                                                handleContextMenu(e, emotionKey);
                                            }}
                                            onMouseEnter={() => handleMouseEnter(emotionKey)}
                                            onMouseLeave={hideTooltip}
                                        >
                                            {emotionKey}
                                        </button>

                                        {showTooltip && (
                                            <div className="es-tooltip" onClick={(e) => e.stopPropagation()}>
                                                <div className="es-tooltip-title">
                                                    {emotionKey}{' '}
                                                    <span style={{ fontWeight: 'normal', fontSize: '0.8rem', color: '#ddd' }}>
                                                        {dictionaryCache[emotionKey] && !dictionaryCache[emotionKey].error && !dictionaryCache[emotionKey].loading
                                                            ? dictionaryCache[emotionKey].phonetic
                                                            : (emotionInfo.phonetic || '')}
                                                    </span>
                                                </div>

                                                {(() => {
                                                    const defs = (dictionaryCache[emotionKey] && !dictionaryCache[emotionKey].error && !dictionaryCache[emotionKey].loading)
                                                        ? dictionaryCache[emotionKey].definitions
                                                        : (emotionInfo.definitions || []);

                                                    if (defs.length > 0) {
                                                        return defs.map((def, idx) => (
                                                            <div key={idx} className="es-tooltip-definition">
                                                                • {def}
                                                            </div>
                                                        ));
                                                    } else if (!dictionaryCache[emotionKey] || dictionaryCache[emotionKey]?.loading) {
                                                        return <div className="es-tooltip-definition" style={{ fontStyle: 'italic', color: '#888' }}>Loading definition...</div>;
                                                    } else {
                                                        return <div className="es-tooltip-definition" style={{ fontStyle: 'italic', color: '#888' }}>No definition available.</div>;
                                                    }
                                                })()}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>


                    </div>
                </div>

                <div style={{ flex: 1, background: 'transparent' }}>
                    <ReflectionForm selectedEmotionProp={selectedEmotions.length > 0 ? selectedEmotions.join(', ') : '...'} isSideBySide={true} dateStrProp={dateStr} moodTypeProp={moodType} />
                </div>
            </div>


        </div>
    );
}
